import React, { useState } from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  Checkbox,
  IconButton,
  List,
  ListItem,
  ListItemText,
  InputAdornment,
  Paper,
  Autocomplete,
} from "@mui/material";
import {
  Close as CloseIcon,
  Search as SearchIcon,
  DeleteOutline as DeleteIcon,
} from "@mui/icons-material";

import { SAMPLE_CODES } from "./test-data";

const UserTerritories = ({
  options = [],
  initialCodes = [],
  onAdd,
  onRemove,
}) => {
  const normalizeToObject = (val) =>
    typeof val === "string" ? { code: val } : val || {};

  const [codes, setCodes] = useState(
    Array.isArray(initialCodes) ? initialCodes.map(normalizeToObject) : []
  );
  const [inputValue, setInputValue] = useState("");
  const [selectedOption, setSelectedOption] = useState(null);
  const [selectedIds, setSelectedIds] = useState(new Set());

  const getCodeValue = (o) =>
    (o &&
      (o.code ||
        o.salesTerritoryCode ||
        o.SalesTerritoryCode ||
        o.territory ||
        o.Territory)) ||
    (typeof o === "string" ? o : "");

  const getOptionId = (o) => getCodeValue(o) || JSON.stringify(o);
  const getOptionLabel = (o) => getCodeValue(o) || "";

  const addCode = (option) => {
    if (!option) return;
    const codeVal = getCodeValue(option);
    if (!codeVal) return;
    const exists = codes.some((c) => getCodeValue(c) === codeVal);
    if (exists) {
      setSelectedOption(null);
      setInputValue("");
      return;
    }
    const next = [...codes, normalizeToObject(option)];
    setCodes(next);
    setSelectedOption(null);
    setInputValue("");
    if (onAdd) onAdd(option, next);
  };

  const toggleSelect = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleBulkRemove = () => {
    if (selectedIds.size === 0) return;
    const idsToRemove = new Set(selectedIds);
    const removed = codes.filter((c) => idsToRemove.has(getOptionId(c)));
    const next = codes.filter((c) => !idsToRemove.has(getOptionId(c)));
    setCodes(next);
    setSelectedIds(new Set());
    if (onRemove) removed.forEach((r) => onRemove(r, next));
  };

  const clearSearch = () => {
    setInputValue("");
    setSelectedOption(null);
  };

  const optionList = options && options.length ? options : SAMPLE_CODES;

  return (
    <Paper
      elevation={0}
      sx={{ p: 2, height: "100%", position: "relative", pb: 8 }}
    >
      <Box
        sx={{
          display: "flex",
          gap: { xs: 1, md: 2 },
          mb: 2,
          width: "100%",
          flexDirection: { xs: "column", lg: "row" },
          alignItems: { md: "center" },
        }}
      >
        <Autocomplete
          disablePortal
          options={optionList}
          value={selectedOption}
          inputValue={inputValue}
          onInputChange={(_, v) => setInputValue(v)}
          onChange={(_, v) => addCode(v)}
          getOptionLabel={getOptionLabel}
          renderOption={(props, option) => (
            <ListItem
              {...props}
              style={{ fontSize: "1.25rem", paddingTop: 10, paddingBottom: 10 }}
            >
              <ListItemText primary={getOptionLabel(option)} />
            </ListItem>
          )}
          isOptionEqualToValue={(o, v) => getOptionId(o) === getOptionId(v)}
          sx={{ width: { xs: "100%", lg: 380 } }}
          renderInput={(params) => (
            <TextField
              {...params}
              placeholder="Search territory codes"
              sx={{
                "& .MuiOutlinedInput-root": {
                  height: "40px",
                  fontSize: "14px",
                },
                "& .MuiInputBase-input": {
                  fontSize: "1.25rem",
                  height: "40px",
                },
              }}
              slotProps={{
                input: {
                  ...params.InputProps,
                  startAdornment: (
                    <>
                      <InputAdornment position="start">
                        <SearchIcon sx={{ color: "#666", fontSize: "20px" }} />
                      </InputAdornment>
                      {params.InputProps.startAdornment}
                    </>
                  ),
                  endAdornment: (
                    <>
                      {inputValue ? (
                        <InputAdornment position="end">
                          <IconButton
                            size="small"
                            onClick={clearSearch}
                            sx={{ color: "#666" }}
                          >
                            <CloseIcon sx={{ fontSize: "18px" }} />
                          </IconButton>
                        </InputAdornment>
                      ) : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                },
              }}
            />
          )}
        />
      </Box>

      <Typography
        variant="subtitle1"
        sx={{ mb: 1, color: "#666", fontSize: "1.3rem" }}
      >
        Territories:
      </Typography>

      {codes.length === 0 ? (
        <Typography color="text.secondary">No territories added.</Typography>
      ) : (
        <List sx={{ bgcolor: "transparent" }}>
          {codes.map((codeObj) => {
            const id = getOptionId(codeObj);
            const label = getOptionLabel(codeObj);
            return (
              <ListItem key={id} divider sx={{ pl: 0 }}>
                <Checkbox
                  edge="start"
                  checked={selectedIds.has(id)}
                  onChange={() => toggleSelect(id)}
                  sx={{ mr: 1, "& .MuiSvgIcon-root": { fontSize: 20 } }}
                />

                <ListItemText
                  primary={label}
                  slotProps={{
                    primary: {
                      fontSize: "1.35rem",
                      id: `territory-${id}`,
                    },
                  }}
                />
              </ListItem>
            );
          })}
        </List>
      )}

      <Box sx={{ position: "absolute", right: 16, bottom: 16 }}>
        <Button
          variant="contained"
          color="error"
          onClick={handleBulkRemove}
          startIcon={<DeleteIcon />}
          size="medium"
          disabled={selectedIds.size === 0}
          sx={{ fontSize: "1.25rem", width: { xs: "100%", lg: "auto" } }}
        >
          {`Remove Selected (${selectedIds.size})`}
        </Button>
      </Box>
    </Paper>
  );
};

export default UserTerritories;
